import React, { useMemo } from "react"
import {
  Table,
  Space,
  Typography,
  Button,
  Tag,
  Dropdown,
  Avatar,
  message,
} from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import {
  UserOutlined,
  ClockCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
} from "@mimber/ui-web"
import { useRoleAssignments } from "@mimber/client/mimber-organization"
import { useRevokeRoleAssignmentModal } from "@/components/modals"
import type { RoleAssignmentWithRelationsDto } from "@mimber/schema"
import { formatTableDate } from "@mimber/ui-web"
import type { RoleAssignmentsTableProps } from "./RoleAssignmentsTable.types"

const { Text } = Typography

export const RoleAssignmentsTable: React.FunctionComponent<
  RoleAssignmentsTableProps
> = ({ roleUuid, onRowClick, className }) => {
  const { data: response, isLoading, error } = useRoleAssignments(roleUuid)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load role assignments: ${error.message}`)
    }
  }, [error])

  const { contextHolder, showRevokeRoleAssignmentModal } =
    useRevokeRoleAssignmentModal(roleUuid)

  const handleRevoke = (record: RoleAssignmentWithRelationsDto) => {
    showRevokeRoleAssignmentModal({ assignment: record })
  }

  const columns: ColumnsType<RoleAssignmentWithRelationsDto> = useMemo(
    () => [
      {
        title: "Member",
        key: "member",
        render: (_, record) => (
          <Space>
            <Avatar icon={<UserOutlined />} size="default" />
            <div>
              <div>
                <Text strong>
                  {record.membership.user.firstName &&
                  record.membership.user.lastName
                    ? `${record.membership.user.firstName} ${record.membership.user.lastName}`
                    : record.membership.user.email}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {record.membership.user.email}
                </Text>
              </div>
            </div>
          </Space>
        ),
      },
      {
        title: "Expires",
        key: "expiresAt",
        render: (_, record) => {
          if (!record.expiresAt) return <Text type="secondary">Never</Text>
          const isExpired = new Date(record.expiresAt) < new Date()
          return (
            <Space>
              <ClockCircleOutlined />
              <Text type={isExpired ? "danger" : undefined}>
                {formatTableDate(record.expiresAt)}
              </Text>
              {isExpired && <Tag color="red">Expired</Tag>}
            </Space>
          )
        },
      },
      {
        title: "Assigned",
        key: "createdAt",
        render: (_, record) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(record.createdAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_, record) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{
                items: [
                  {
                    key: "revoke",
                    label: "Revoke",
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => handleRevoke(record),
                  },
                ],
              }}
              trigger={["click"]}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          </div>
        ),
      },
    ],
    [handleRevoke]
  )

  return (
    <>
      {contextHolder}
      <Table<RoleAssignmentWithRelationsDto>
        bordered
        columns={columns}
        dataSource={response?.data ?? []}
        loading={isLoading}
        rowKey="uuid"
        pagination={false}
        className={className}
        locale={{ emptyText: "No members assigned" }}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
      />
    </>
  )
}
